import { useMemo } from 'react';

import { useGetOnDevicePaymentInstruments } from './useGetOnDevicePaymentInstruments';
import { PaymentInstrumentFragment, useGetPaymentInstrumentsQuery } from '../../graphql/types.generated';
import {
  isGoogleOrApplePaymentInstrument,
  isOpenLoopPaymentInstrument,
  isPaymentInstrumentActive,
  mapPaymentInstrumentFragmentToPaymentInstrument,
} from './utils';
import { PaymentInstrument } from './types';
import { useConfiguration } from '../configuration/useConfiguration';

export interface IUsePaymentInstrumentsReturns {
  loading: boolean;
  refetch(): Promise<any>;
  hasPasscode: boolean;
  paymentInstruments: PaymentInstrument[];
  defaultPaymentInstrumentId: string | null;
  isWalletSizeLimitReached: boolean;
  walletSizeLimit: number;
  isWalletEnabled: boolean;
}

const isDefaultPaymentInstrument = ({ isDefault }: PaymentInstrumentFragment): boolean => !!isDefault;

type UsePaymentInstrumentsParams = {
  showOnDevicePaymentInstruments?: boolean;
};

export const usePaymentInstruments = (params: UsePaymentInstrumentsParams = {}): IUsePaymentInstrumentsReturns => {
  const { showOnDevicePaymentInstruments = true } = params;

  // isWalletEnabled === showSavedPaymentInstruments
  const { sizeLimit: walletSizeLimit, enabled: isWalletEnabled } = useConfiguration('wallet');

  const { data, loading, refetch } = useGetPaymentInstrumentsQuery({
    skip: !isWalletEnabled,
    fetchPolicy: 'cache-and-network',
  });

  const activePaymentInstruments = useMemo(() => {
    if (!data?.customer?.paymentWallet) {
      return [];
    }
    return data.customer.paymentWallet.filter<PaymentInstrumentFragment>(isPaymentInstrumentActive);
  }, [data?.customer?.paymentWallet]);
  const { loading: onDevicePaymentInstrumentsLoading, paymentInstruments: onDeviceInstruments } =
    useGetOnDevicePaymentInstruments({ skip: !showOnDevicePaymentInstruments });

  const defaultPaymentInstrumentId: string | null = useMemo(() => {
    const defaultInstrument = activePaymentInstruments.find(isDefaultPaymentInstrument);
    const onDeviceDefaultInstrument = onDeviceInstruments.find(
      ({ paymentType }) => paymentType === defaultInstrument?.paymentType,
    );

    // Return Apple Pay for iOS as default
    // Return Google Pay for Android
    return onDeviceDefaultInstrument?.uuid || defaultInstrument?.uuid || onDeviceInstruments[0]?.uuid || null;
  }, [activePaymentInstruments, onDeviceInstruments]);

  const paymentInstruments = useMemo(() => {
    /**
     * Query returns APPLE / GOOGLE Pay as a payment instrument, even if it's not available on device
     * It was implemented that way to make it possible to use 'idDefault' logic
     */
    const paymentInstrumentsWithoutOnDeviceWallets = activePaymentInstruments.filter(
      ({ paymentType }) => !isGoogleOrApplePaymentInstrument(paymentType),
    );

    const mappedPaymentInstrumentsWithoutOnDeviceWallets = paymentInstrumentsWithoutOnDeviceWallets.map(
      mapPaymentInstrumentFragmentToPaymentInstrument,
    );

    if (!showOnDevicePaymentInstruments && !isWalletEnabled) {
      return [];
    }

    if (!showOnDevicePaymentInstruments) {
      return mappedPaymentInstrumentsWithoutOnDeviceWallets;
    }

    if (!isWalletEnabled) {
      return onDeviceInstruments;
    }

    return [...onDeviceInstruments, ...mappedPaymentInstrumentsWithoutOnDeviceWallets];
  }, [activePaymentInstruments, onDeviceInstruments, showOnDevicePaymentInstruments, isWalletEnabled]);

  return useMemo(() => {
    const openLoopCardsAmount = activePaymentInstruments.filter(({ paymentType }) =>
      isOpenLoopPaymentInstrument(paymentType),
    ).length;
    return {
      paymentInstruments,
      refetch,
      defaultPaymentInstrumentId,
      loading: loading || onDevicePaymentInstrumentsLoading,
      hasPasscode: data?.customer?.hasPasscode ?? false,
      isWalletSizeLimitReached: openLoopCardsAmount >= walletSizeLimit,
      walletSizeLimit,
      isWalletEnabled,
    };
  }, [
    activePaymentInstruments,
    paymentInstruments,
    refetch,
    defaultPaymentInstrumentId,
    loading,
    onDevicePaymentInstrumentsLoading,
    data?.customer?.hasPasscode,
    walletSizeLimit,
    isWalletEnabled,
  ]);
};
